/**
 * The Space weather pages' small charts as geometry (docs/specs/space-weather.md §4.3 D, E): the solar wind traces
 * (speed, density, Bt and Bz) and NOAA's 3-day Kp bars, in a 600-wide view box, with their vertical scales (marks,
 * words and gridlines), time marks and text alternative. Teslyn Field and Teslyn Cloud draw the same shapes with their
 * own styles, so both pages show the same chart for the same answers. Pure.
 */

/** The charts' view box width (the SVG stretches to its box). */
export const SW_CHART_WIDTH = 600;

/** A mark on a chart's vertical scale: its value, its height in the view box and its words. */
export interface AxisTick {
  readonly value: number;
  readonly y: number;
  readonly label: string;
}

/** A mark on a chart's time axis: its place across the view box (0 to 600) and its words. */
export interface TimeTick {
  readonly x: number;
  readonly label: string;
}

const STEPS = [1, 2, 2.5, 5, 10];

/**
 * A readable vertical scale for values from `lo` to `hi`: a step of 1, 2, 2.5 or 5 times a power of ten giving about
 * `target` intervals, the range widened to whole steps (so the lowest and highest marks sit on the frame).
 */
export function niceScale(
  lo: number,
  hi: number,
  target = 4,
): { readonly lo: number; readonly hi: number; readonly step: number; readonly ticks: readonly number[] } {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lo: 0, hi: 1, step: 1, ticks: [0, 1] };
  if (hi < lo) [lo, hi] = [hi, lo];
  if (hi - lo < 1e-9) {
    const pad = Math.abs(lo) > 1e-9 ? Math.abs(lo) * 0.1 : 1;
    lo -= pad;
    hi += pad;
  }
  const raw = (hi - lo) / Math.max(1, target);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = (STEPS.find((k) => k * mag >= raw - 1e-12) ?? 10) * mag;
  const a = Number((Math.floor(lo / step + 1e-9) * step).toFixed(10));
  const b = Number((Math.ceil(hi / step - 1e-9) * step).toFixed(10));
  const ticks: number[] = [];
  for (let i = 0; a + i * step <= b + step / 2; i++) ticks.push(Number((a + i * step).toFixed(10)));
  return { lo: a, hi: b, step, ticks };
}

/** A scale mark's words, with as many decimals as the step has (5, 2.5, 0.2). */
export function tickLabel(v: number, step: number): string {
  let decimals = 0;
  while (decimals < 3 && Math.abs(step * 10 ** decimals - Math.round(step * 10 ** decimals)) > 1e-9)
    decimals++;
  const t = Math.abs(v) < step / 1000 ? 0 : v;
  return t.toFixed(decimals).replace('-', '−');
}

/** Time marks from `fromMs` to `toMs`: every `everyH` hours on the UTC clock, worded by `words`. */
function timeTicks(
  fromMs: number,
  toMs: number,
  everyH: number,
  words: (ms: number) => string,
): readonly TimeTick[] {
  const span = Math.max(1, toMs - fromMs);
  const every = everyH * 3_600_000;
  const out: TimeTick[] = [];
  for (let t = Math.ceil(fromMs / every) * every; t <= toMs; t += every)
    out.push({ x: ((t - fromMs) / span) * SW_CHART_WIDTH, label: words(t) });
  return out;
}

/** "18:00" (UTC). */
const hourWords = (ms: number): string => new Date(ms).toISOString().slice(11, 16);

type Pt = { readonly t: number; readonly v: number | null };

export interface TraceGeometry {
  /** One path per run of points (a gap of more than 15 minutes, or a missing value, breaks the line). */
  readonly paths: readonly string[];
  /** The second line's paths, when one is given (Bt beside Bz). */
  readonly paths2: readonly string[];
  /** The zero line's y, when asked for (Bz: south below it). */
  readonly zeroY: number | null;
  /** The limit line's y, when a limit is given. */
  readonly limitY: number | null;
  /** Shaded time bands as x and width. */
  readonly bands: readonly { readonly x: number; readonly width: number }[];
  /** The vertical scale, bottom to top, with a gridline at each mark. */
  readonly ticks: readonly AxisTick[];
  /** Every 6 hours on the UTC clock. */
  readonly times: readonly TimeTick[];
  /** "Speed, 24 h (km/s): from 290.0 to 420.0 km/s, latest 320.0 km/s." */
  readonly description: string;
}

/** A line (or two on one scale) over time with a vertical scale and optional zero line, limit line and bands. */
export function traceGeometry(input: {
  readonly label: string;
  readonly unit: string;
  readonly points: readonly Pt[];
  /** A second line on the same scale (Bt beside Bz). */
  readonly points2?: readonly Pt[];
  readonly fromMs: number;
  readonly toMs: number;
  readonly height: number;
  readonly zero?: boolean;
  readonly limit?: number | null;
  readonly bands?: readonly { readonly from: number; readonly to: number }[];
}): TraceGeometry {
  const { points, fromMs, toMs, height } = input;
  const W = SW_CHART_WIDTH;
  const finite = (ps: readonly Pt[]): number[] =>
    ps
      .filter((p) => p.t >= fromMs && p.t <= toMs)
      .map((p) => p.v)
      .filter((v): v is number => v !== null && Number.isFinite(v));
  const vals = finite(points);
  const all = [...vals, ...finite(input.points2 ?? [])];
  let lo = all.length ? Math.min(...all) : 0;
  let hi = all.length ? Math.max(...all) : 1;
  if (input.zero) {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  const limit = input.limit ?? null;
  if (limit !== null) {
    hi = Math.max(hi, limit * 1.15);
    lo = Math.min(lo, 0);
  }
  // a scale of whole steps round the values: its lowest and highest marks are the frame's bottom and top
  const scale = niceScale(lo, hi, 4);
  lo = scale.lo;
  hi = scale.hi;
  const span = Math.max(1, toMs - fromMs);
  const x = (t: number): number => ((t - fromMs) / span) * W;
  const y = (v: number): number => height - ((v - lo) / (hi - lo)) * height;
  const lines = (ps: readonly Pt[]): string[] => {
    const paths: string[] = [];
    let cur = '';
    let lastT: number | null = null;
    for (const p of ps) {
      if (p.v === null || !Number.isFinite(p.v) || p.t < fromMs || p.t > toMs) {
        if (cur) paths.push(cur);
        cur = '';
        lastT = null;
        continue;
      }
      // a gap of more than 15 minutes (a missing 10-minute mean) breaks the line
      if (lastT !== null && p.t - lastT > 15 * 60_000 && cur) {
        paths.push(cur);
        cur = '';
      }
      cur += `${cur ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`;
      lastT = p.t;
    }
    if (cur) paths.push(cur);
    return paths;
  };
  const last = vals.length ? vals[vals.length - 1]! : null;
  const description = vals.length
    ? `${input.label}: from ${Math.min(...vals).toFixed(1)} to ${Math.max(...vals).toFixed(1)} ${input.unit}, latest ${last!.toFixed(1)} ${input.unit}.`
    : `${input.label}: no data.`;
  return {
    paths: lines(points),
    paths2: lines(input.points2 ?? []),
    zeroY: input.zero ? y(0) : null,
    limitY: limit !== null ? y(limit) : null,
    bands: (input.bands ?? []).map((b) => ({ x: x(b.from), width: Math.max(1, x(b.to) - x(b.from)) })),
    ticks: scale.ticks.map((v) => ({ value: v, y: y(v), label: tickLabel(v, scale.step) })),
    times: timeTicks(fromMs, toMs, 6, hourWords),
    description,
  };
}

export interface KpBarsGeometry {
  readonly bars: readonly {
    readonly t: string;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    /** At or above the project's "Exceedances likely" Kp. */
    readonly high: boolean;
  }[];
  readonly daylight: readonly { readonly x: number; readonly width: number }[];
  readonly likelyY: number;
  /** "Exceedances likely from Kp 5": the dashed line's words. */
  readonly likelyLabel: string;
  /** Kp 0 to 9, bottom to top, with a gridline at each. */
  readonly ticks: readonly AxisTick[];
  /** Each UTC day's start ("Mon 26"). */
  readonly times: readonly TimeTick[];
  /** The "now" line's x, when now is inside the three days. */
  readonly nowX: number | null;
  readonly description: string;
}

/** NOAA's Kp by 3 hours for three days from `fromMs`, with the "Exceedances likely" Kp and daylight marked. */
export function kpBarsGeometry(input: {
  readonly bins: readonly { readonly t: string; readonly kp: number }[];
  readonly likelyKp: number;
  readonly daylight: readonly (readonly [number, number])[];
  readonly fromMs: number;
  readonly nowMs: number;
  readonly height: number;
}): KpBarsGeometry {
  const { fromMs, height: h } = input;
  const W = SW_CHART_WIDTH;
  const span = 3 * 86_400_000;
  const x = (t: number): number => ((t - fromMs) / span) * W;
  const y = (kp: number): number => h - (Math.min(9, kp) / 9) * h;
  const bw = (3 * 3_600_000 * W) / span;
  const shown = input.bins.filter((b) => {
    const t = Date.parse(b.t);
    return t >= fromMs && t < fromMs + span;
  });
  const max = shown.length ? Math.max(...shown.map((b) => b.kp)) : null;
  return {
    bars: shown.map((b) => {
      const t = Date.parse(b.t);
      return {
        t: b.t,
        x: x(t) + 1,
        y: y(b.kp),
        width: Math.max(1, bw - 2),
        height: h - y(b.kp),
        high: b.kp >= input.likelyKp,
      };
    }),
    daylight: input.daylight.map(([a, b]) => ({ x: x(a), width: Math.max(0, x(b) - x(a)) })),
    likelyY: y(input.likelyKp),
    likelyLabel: `Exceedances likely from Kp ${input.likelyKp}`,
    ticks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((k) => ({ value: k, y: y(k), label: String(k) })),
    times: timeTicks(fromMs, fromMs + span - 1, 24, (ms) =>
      new Date(ms).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', timeZone: 'UTC' }),
    ),
    nowX: input.nowMs >= fromMs && input.nowMs < fromMs + span ? x(input.nowMs) : null,
    description:
      max === null
        ? 'Kp forecast: none held.'
        : `Kp forecast by 3 hours for three days: at most ${max.toFixed(2)}; your "Exceedances likely" level is Kp ${input.likelyKp}.`,
  };
}
